import React, { useEffect, useState } from "react";
import { Copy, X, Key, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Popup persistente in basso a sinistra che mostra le credenziali appena recuperate
 * per un fornitore. L'utente clicca "Copia password" → password copiata negli appunti.
 * Pulsante "occhio" per mostrare/nascondere la password in chiaro (necessario per
 * lettura manuale o trascrizione).
 *
 * Si attiva via evento custom DOM "archihub:show-creds" con detail
 * `{ supplierName, url, username, password, has_password }`.
 * Si auto-chiude dopo 2 minuti per sicurezza.
 */
export default function SupplierCredsPopup() {
  const [creds, setCreds] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    function onShow(e) {
      setCreds(e.detail);
      setShowPwd(false);  // reset visibilità ad ogni nuova credenziale
    }
    window.addEventListener("archihub:show-creds", onShow);
    return () => window.removeEventListener("archihub:show-creds", onShow);
  }, []);

  // Auto-close dopo 2 minuti
  useEffect(() => {
    if (!creds) return;
    const t = setTimeout(() => setCreds(null), 120000);
    return () => clearTimeout(t);
  }, [creds]);

  if (!creds) return null;

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(creds.password || "");
      toast.success("Password copiata · ora incolla nel campo password del sito", { duration: 5000 });
    } catch {
      toast.error("Impossibile copiare. Password: " + creds.password);
    }
  }

  async function copyUsername() {
    try {
      await navigator.clipboard.writeText(creds.username || "");
      toast.success("Username copiato");
    } catch { /* ignore */ }
  }

  return (
    <div
      className="fixed bottom-5 left-5 z-[100] bg-ink text-cream rounded-sm shadow-2xl border border-gold/40 w-[360px]"
      data-testid="supplier-creds-popup"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-cream/15">
        <div className="text-[10px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1.5">
          <Key size={11} /> Credenziali · {creds.supplierName}
        </div>
        <button
          onClick={() => setCreds(null)}
          className="text-cream/70 hover:text-cream"
          data-testid="supplier-creds-popup-close"
          aria-label="Chiudi"
        >
          <X size={14} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="text-[9px] tracking-[0.2em] uppercase text-cream/60">Username</div>
          <div className="flex items-center gap-2 mt-0.5">
            <code className="flex-1 text-sm bg-cream/10 px-2 py-1 rounded-sm truncate" title={creds.username}>
              {creds.username || "—"}
            </code>
            <button
              onClick={copyUsername}
              className="px-2 py-1 bg-cream/10 hover:bg-cream/20 rounded-sm inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase"
              data-testid="supplier-creds-copy-username"
            >
              <Copy size={11} /> Copia
            </button>
          </div>
        </div>
        <div>
          <div className="text-[9px] tracking-[0.2em] uppercase text-cream/60">Password</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <code
              className="flex-1 text-sm bg-cream/10 px-2 py-1 rounded-sm truncate font-mono"
              title={showPwd && creds.has_password ? creds.password : ""}
              data-testid="supplier-creds-password-display"
            >
              {creds.has_password ? (showPwd ? creds.password : "••••••••••••") : "— nessuna —"}
            </code>
            <button
              onClick={() => setShowPwd((v) => !v)}
              disabled={!creds.has_password}
              className="p-1.5 bg-cream/10 hover:bg-cream/20 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
              title={showPwd ? "Nascondi password" : "Mostra password in chiaro"}
              data-testid="supplier-creds-toggle-password"
              aria-label={showPwd ? "Nascondi password" : "Mostra password"}
            >
              {showPwd ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
            <button
              onClick={copyPassword}
              disabled={!creds.has_password}
              className="px-2 py-1 bg-gold hover:bg-gold/90 text-ink rounded-sm inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="supplier-creds-copy-password"
            >
              <Copy size={11} /> Copia
            </button>
          </div>
        </div>
        <div className="text-[10px] text-cream/60 italic">Auto-chiusura tra 2 minuti. Click 👁 per leggere la password, &ldquo;Copia&rdquo; per incollare nel form del sito fornitore.</div>
      </div>
    </div>
  );
}
